import { useState } from "react";
import { Plus, Pencil, Trash2, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useWellnessState } from "@/context/WellnessStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { iso, uid, fmtDay, periodLabel } from "@/lib/wellness";
import { periodKey } from "@/lib/life";
import { PageHeader, SectionCard, KpiCard, EmptyState, AddButton } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { FormModal, TextField, NumberField, TextArea } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Progress() {
  const { state, update, loading } = useWellnessState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const allChecks = (state.checkIns || []).slice().sort((a, b) => (a.loggedOn > b.loggedOn ? 1 : -1));
  const activeKey = periodKey(horizon, date);
  const checks = allChecks.filter((c) => periodKey(horizon, c.loggedOn) === activeKey);
  const trend = checks.map((c) => ({ label: fmtDay(c.loggedOn), weight: Number(c.weight) || null }));
  const first = checks[0], last = checks[checks.length - 1];
  const delta = first && last ? (last.weight - first.weight) : 0;

  const openNew = () => { setDraft({ id: uid("c"), loggedOn: horizon === "Day" ? date : iso(), weight: "", waist: "", energy: 7, sleepHours: "", waterOz: "", workouts: "", notes: "" }); setOpen(true); };
  const openEdit = (c) => { setDraft({ ...c }); setOpen(true); };
  const save = () => {
    const item = { ...draft, weight: Number(draft.weight) || 0, waist: Number(draft.waist) || 0, energy: Number(draft.energy) || 0, sleepHours: Number(draft.sleepHours) || 0, waterOz: Number(draft.waterOz) || 0, workouts: Number(draft.workouts) || 0 };
    update((prev) => { const exists = (prev.checkIns || []).some((x) => x.id === item.id); return { ...prev, checkIns: exists ? prev.checkIns.map((x) => x.id === item.id ? item : x) : [...(prev.checkIns || []), item] }; }, "Logged a check-in");
    setOpen(false); toast.success("Check-in saved");
  };
  const remove = (id) => { update((prev) => ({ ...prev, checkIns: prev.checkIns.filter((c) => c.id !== id) }), "Removed check-in"); toast.success("Removed"); };

  return (
    <div>
      <PageHeader title="Progress" subtitle="Track the evidence — measurements, energy, sleep and workouts over time." testid="wellness-progress-header"
        actions={<AddButton onClick={openNew} testid="add-checkin-button"><Plus className="h-4 w-4 mr-1.5" />Log check-in</AddButton>} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="progress-period" />
        <span className="text-sm text-[var(--text-muted)]" data-testid="progress-period-label">Showing {periodLabel(horizon, date)} · {checks.length} check-in{checks.length === 1 ? "" : "s"}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Latest weight" value={last?.weight ? `${last.weight} lb` : "—"} sub={last ? fmtDay(last.loggedOn) : "No data this period"} accent testid="kpi-latest-weight" />
        <KpiCard label="Change" value={`${delta > 0 ? "+" : ""}${Math.round(delta * 10) / 10} lb`} sub="Within this period" testid="kpi-weight-change" />
        <KpiCard label="Latest energy" value={last?.energy ? `${last.energy}/10` : "—"} sub="Self-reported" testid="kpi-latest-energy" />
        <KpiCard label="Check-ins" value={checks.length} sub="This period" testid="kpi-checkins" />
      </div>

      <SectionCard title="Weight trend" testid="weight-trend-card" className="mb-4">
        {trend.length < 2 ? <EmptyState icon={LineIcon} title="Not enough data yet" message="Log two or more check-ins to see your trend." /> : (
          <div className="h-64" data-testid="weight-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
              <LineChart data={trend} margin={{ top: 10, right: 10, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(23,35,31,0.08)" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
                <YAxis domain={["dataMin - 3", "dataMax + 3"]} tick={{ fontSize: 12, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--border-subtle)", fontSize: 13 }} />
                <Line type="monotone" dataKey="weight" stroke="var(--green)" strokeWidth={2.5} dot={{ r: 3, fill: "var(--green)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Check-in history" testid="checkin-history-card">
        {checks.length === 0 ? <EmptyState icon={LineIcon} title="No check-ins yet" message="Log your first check-in to start tracking." /> : (
          <div className="space-y-2">
            {checks.slice().reverse().map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`checkin-row-${c.id}`}>
                <span className="text-xs tabular-nums text-[var(--text-muted)] w-16 shrink-0">{fmtDay(c.loggedOn)}</span>
                <div className="min-w-0 flex-1 text-[13px] text-[var(--text)]">{c.weight} lb · waist {c.waist}in · {c.sleepHours}h sleep · {c.waterOz}oz · {c.workouts} workouts · energy {c.energy}/10{c.notes ? <span className="text-[var(--text-muted)]"> — {c.notes}</span> : null}</div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`checkin-edit-${c.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => remove(c.id)} data-testid={`checkin-delete-${c.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {draft && (
        <FormModal open={open} onOpenChange={setOpen} title={(state.checkIns || []).some((x) => x.id === draft.id) ? "Edit check-in" : "Log check-in"} onSubmit={save} testid="checkin-modal">
          <TextField label="Date" type="date" value={draft.loggedOn} onChange={(e) => setDraft({ ...draft, loggedOn: e.target.value })} testid="checkin-date" />
          <NumberField label="Weight (lb)" value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: e.target.value })} testid="checkin-weight" />
          <NumberField label="Waist (in)" value={draft.waist} onChange={(e) => setDraft({ ...draft, waist: e.target.value })} testid="checkin-waist" />
          <NumberField label="Energy (1-10)" value={draft.energy} onChange={(e) => setDraft({ ...draft, energy: e.target.value })} testid="checkin-energy" />
          <NumberField label="Sleep (hours)" value={draft.sleepHours} onChange={(e) => setDraft({ ...draft, sleepHours: e.target.value })} testid="checkin-sleep" />
          <NumberField label="Water (oz)" value={draft.waterOz} onChange={(e) => setDraft({ ...draft, waterOz: e.target.value })} testid="checkin-water" />
          <NumberField label="Workouts (this week)" value={draft.workouts} onChange={(e) => setDraft({ ...draft, workouts: e.target.value })} testid="checkin-workouts" />
          <TextArea label="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} full testid="checkin-notes" />
        </FormModal>
      )}
    </div>
  );
}
